"use client";

import {
  MutationCache,
  QueryCache,
  QueryClient,
  QueryClientProvider,
} from "@tanstack/react-query";
import dynamic from "next/dynamic";
import { useEffect, useState } from "react";
import { handleAuthError, setAuthRecoveryHandler } from "@/lib/auth-redirect";
import { MobileNavProvider } from "@/components/layout/MobileNavContext";
import { useDoubleTapContextMenu } from "@/hooks/use-double-tap-context-menu";
import SimulationProcessingHost from "@/components/simulation/SimulationProcessingHost";

const ResponseFormPopupHost = dynamic(
  () => import("@/components/response-templates/ResponseFormPopupHost"),
  { ssr: false },
);

function DoubleTapContextMenuHost() {
  useDoubleTapContextMenu();
  return null;
}

export default function Providers({ children }: { children: React.ReactNode }) {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        // Any query/mutation that fails with a 401 (session expired) redirects to
        // the login screen with a message, so actions never silently no-op.
        queryCache: new QueryCache({
          onError: (error) => handleAuthError(error),
        }),
        mutationCache: new MutationCache({
          onError: (error) => handleAuthError(error),
        }),
        defaultOptions: {
          queries: {
            retry: 1,
            // Keep server data in memory across sidebar routes (Pods, Products, Simulation, …).
            staleTime: 5 * 60 * 1000,
            gcTime: 24 * 60 * 60 * 1000,
            // Avoid refetch storms when switching browser tabs; cache stays until stale.
            refetchOnWindowFocus: false,
          },
        },
      })
  );

  useEffect(() => {
    setAuthRecoveryHandler(() => {
      void queryClient.invalidateQueries();
    });
    return () => setAuthRecoveryHandler(null);
  }, [queryClient]);

  return (
    <QueryClientProvider client={queryClient}>
      <MobileNavProvider>
        {children}
        <DoubleTapContextMenuHost />
        <ResponseFormPopupHost />
        <SimulationProcessingHost />
      </MobileNavProvider>
    </QueryClientProvider>
  );
}
